'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Card, CardContent, CardHeader, Stack } from '@packages/shared';
import { useIntl } from 'react-intl';
import { UspsCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/UspsCardFragmentFragmentDoc';
import { Checkmark32 } from '@packages/themes/icons';
import { uspsCardMessages, uspsMessages } from './messages';
import { AdditionalInfoItem } from '../AdditionalInfoItem';
import { getValidInfoList } from '../getValidInfoList';

/* GraphQL */ `
  fragment UspsCardFragment on UspsInfo {
    infoList {
      serviceLayerId
      translationKey
      iconUrl
    }
  }
`;

export type UspsCardProps = {
  data: FragmentType<typeof UspsCardFragmentFragmentDoc>;
};

export const UspsCard = ({ data }: UspsCardProps) => {
  const { infoList } = getFragmentData(UspsCardFragmentFragmentDoc, data);
  const { formatMessage } = useIntl();

  if (infoList.length === 0) return null;

  const validInfoList = getValidInfoList(infoList, uspsMessages);

  if (validInfoList.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title={formatMessage(uspsCardMessages.cardHeadline)}
        sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
      />
      <CardContent>
        <Stack spacing={1} alignItems="flex-start">
          {validInfoList.map((item, index) => (
            <AdditionalInfoItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              icon={
                item.iconUrl || (
                  <Checkmark32 width="2rem" height="2rem" sx={{ color: 'text.dark' }} />
                )
              }
              serviceLayerId={item.serviceLayerId}
              text={formatMessage(item.messageDescriptor, {
                bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
              })}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
